import {
  Box, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createActiveFilterObject } from '../../../utils';
import { usePositionsGetFilters } from '../../../hooks/usePositions';
import STRING_KEYS from '../../../language/keys';
import MultiSelect from '../../../components/MultiSelect';
import { FilterItem } from '../../../interfaces/filterOptions.interface';
import { ActiveFilters } from '../../../hooks/useTablePageParams';
import { POSITION_PAGE_FILTERS } from '../../../consts';
import TableFiltersItems from '../../../components/Table/TableRenderFiltersItems';
import TableSearch from '../../../components/Table/TableSearch';
import { useAuth } from '../../../hooks/useAuth';
import { PERMISSIONS } from '../../../consts/permissions';
import style from './style';

type Props = {
  activeFilters: ActiveFilters,
  onChangeFilter: (filterName: string, newValue: string[]) => void,
}

const PositionFilters = ({ activeFilters,  onChangeFilter }: Props): JSX.Element => {
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();

  const {
    statusOptions,
    accountOptions,
    onChangeAutoComplete,
    loadAccounts,
    autoCompleteValue,
    stateOptions,
  } = usePositionsGetFilters();

  const handleSearch = (searchValue: string): void => {
    onChangeFilter(POSITION_PAGE_FILTERS.QUERY, [searchValue]);
  };

  const handleAccountsChanged = (newAccounts: string[]): void => {
    onChangeFilter(POSITION_PAGE_FILTERS.ACCOUNTS, newAccounts);
  };

  const handleStatusesChanged = (newStatuses: string[]): void => {
    onChangeFilter(POSITION_PAGE_FILTERS.STATUS, newStatuses);
  };

  const handleStatesChanged = (newStatuses: string[]): void => {
    onChangeFilter(POSITION_PAGE_FILTERS.STATE, newStatuses);
  };

  const handleDelete = (filterItem: FilterItem) => {
    switch (filterItem.field) {
      case POSITION_PAGE_FILTERS.QUERY: {
        handleSearch('');
        break;
      }
      case POSITION_PAGE_FILTERS.ACCOUNTS: {
        handleAccountsChanged(activeFilters[POSITION_PAGE_FILTERS.ACCOUNTS]
          .filter((item: string) => {
            return item !== filterItem.value;
          }));

        break;
      }
      case POSITION_PAGE_FILTERS.STATUS: {
        handleStatusesChanged(activeFilters[POSITION_PAGE_FILTERS.STATUS].filter((item: string) => {
          return item !== filterItem.value;
        }));

        break;
      }
      case POSITION_PAGE_FILTERS.STATE: {
        handleStatesChanged(activeFilters[POSITION_PAGE_FILTERS.STATE].filter((item: string) => {
          return item !== filterItem.value;
        }));

        break;
      }
      default:
        break;
    }
  };

  const getActiveFilters = (): FilterItem[] => {
    const filters: FilterItem[] = [];

    if (activeFilters[POSITION_PAGE_FILTERS.QUERY]) {
      activeFilters[POSITION_PAGE_FILTERS.QUERY].forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              POSITION_PAGE_FILTERS.QUERY,
              translate(STRING_KEYS.FILTER_LABEL_ACCOUNT),
              item,
            ),
          );
        }
      });
    }

    if (activeFilters[POSITION_PAGE_FILTERS.STATUS]) {
      activeFilters[POSITION_PAGE_FILTERS.STATUS].forEach((item: string) => {
        if (item.length > 0) {
          const status = statusOptions.find(({ value }) => value === item);

          filters.push(
            createActiveFilterObject(
              POSITION_PAGE_FILTERS.STATUS,
              translate(STRING_KEYS.FILTER_LABEL_STATUS),
              item,
              status?.label,
            ),
          );
        }
      });
    }

    if (activeFilters[POSITION_PAGE_FILTERS.ACCOUNTS]) {
      activeFilters[POSITION_PAGE_FILTERS.ACCOUNTS].forEach((item: string) => {
        if (item.length > 0) {
          const account = accountOptions.find(({ value }) => value === item);

          filters.push(
            createActiveFilterObject(
              POSITION_PAGE_FILTERS.ACCOUNTS,
              translate(STRING_KEYS.FILTER_LABEL_ACCOUNT),
              item,
              account?.label,
            ),
          );
        }
      });
    }

    if (activeFilters[POSITION_PAGE_FILTERS.STATE]) {
      activeFilters[POSITION_PAGE_FILTERS.STATE].forEach((item: string) => {
        if (item.length > 0) {
          const state = stateOptions.find(({ value }) => value === item);

          filters.push(
            createActiveFilterObject(
              POSITION_PAGE_FILTERS.STATE,
              translate(STRING_KEYS.FILTER_LABEL_STATE),
              item,
              state?.label,
            ),
          );
        }
      });
    }

    return filters;
  };

  return (
    <>
      <TableSearch handleSearch={ handleSearch } />
      <Divider />
      <Box sx={ style.box.sx }>
        {hasPermissions([PERMISSIONS.ACCOUNT_VIEW]) && (
          <MultiSelect
            label={ translate(STRING_KEYS.POSITIONS_LIST_FILTER_ACCOUNT) }
            onChange={ handleAccountsChanged }
            options={ accountOptions }
            value={ activeFilters[POSITION_PAGE_FILTERS.ACCOUNTS] }
            autoComplete
            autoCompleteOnChange={ onChangeAutoComplete }
            autoCompleteValue={ autoCompleteValue }
            isLoading={ loadAccounts }
          />
        )}
        <MultiSelect
          label={ translate(STRING_KEYS.POSITIONS_LIST_FILTER_STATUS) }
          onChange={ handleStatusesChanged }
          options={ statusOptions }
          value={ activeFilters[POSITION_PAGE_FILTERS.STATUS] }
        />
        <MultiSelect
          label={ translate(STRING_KEYS.POSITIONS_LIST_FILTER_STATE) }
          onChange={ handleStatesChanged }
          options={ stateOptions }
          value={ activeFilters[POSITION_PAGE_FILTERS.STATE] }
        />
      </Box>
      <Divider />
      <TableFiltersItems filters={ getActiveFilters() } onDelete={ handleDelete } />
    </>
  );
};

export default PositionFilters;
